import { useEffect, useMemo, useState } from 'react';

import { useAppSelector } from 'src/app/hooks';
import { fetchContactPaymentTotals } from 'src/api/payments/api';
import { ContactPaymentTotal } from 'src/db/entities/payment/PaymentEntity';
import { selectProformaInvoices } from 'src/slices/proformaInvoicesSlice/proformaInvoicesSlice';
import { makeSelectProposalsByContact } from 'src/slices/proposalsSlice';
import { selectCurrentWorkspaceId } from 'src/slices/workspacesSlice';

/**
 * Payments received and total contract value of a contact, one row per currency. They come
 * from the server (it knows which quotes already have an invoice), so the contact itself
 * stays untouched. The request repeats when one of the contact's invoices or quotes changes.
 */
export default function useContactPaymentTotals(contactId: string) {
  const workspaceId = useAppSelector(selectCurrentWorkspaceId);
  const invoices = useAppSelector(selectProformaInvoices);
  const selectProposalsByContact = useMemo(makeSelectProposalsByContact, []);
  const proposals = useAppSelector((state) => selectProposalsByContact(state, contactId));

  const [totals, setTotals] = useState<ContactPaymentTotal[]>([]);

  const changeKey = JSON.stringify([
    invoices
      .filter((invoice) => invoice.contactId === contactId)
      .map(({ uuid, updatedAt, totalPaid, priceWithTax }) => [
        uuid,
        updatedAt,
        totalPaid,
        priceWithTax,
      ]),
    proposals.map(({ uuid, updatedAt }) => [uuid, updatedAt]),
  ]);

  useEffect(() => {
    let ignore = false;

    fetchContactPaymentTotals(contactId, workspaceId)
      .then((result) => {
        if (!ignore) {
          setTotals(result);
        }
      })
      .catch(() => {
        // Totals are informational; the section stays as it was.
      });

    return () => {
      ignore = true;
    };
  }, [contactId, workspaceId, changeKey]);

  return totals;
}
